import { Badge } from "@/components/ui/badge";
import { listPlans } from "@/lib/db/queries/plans";
import { logActivity } from "@/lib/db/audit";
import { getSession } from "@/lib/auth/session";

export const dynamic = "force-dynamic";

const priceFmt = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

function parseFeatures(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed))
      return parsed.filter((x): x is string => typeof x === "string");
    if (parsed && typeof parsed === "object") {
      // Cas {feature_key: true} → on garde uniquement les clés à true
      return Object.entries(parsed as Record<string, unknown>)
        .filter(([, v]) => v === true || v === 1 || v === "1")
        .map(([k]) => k);
    }
  } catch {
    return [];
  }
  return [];
}

export default async function PlansPage() {
  const session = await getSession();
  let rows: Awaited<ReturnType<typeof listPlans>> = [];
  let loadError: string | null = null;
  try {
    rows = await listPlans();
    void logActivity({
      type: "superadmin.plans_list",
      title: "Lecture du référentiel plans",
      description: `${rows.length} plans lus`,
      user_id: session?.user.id,
    });
  } catch (err) {
    loadError = err instanceof Error ? err.message : "Erreur inconnue";
  }

  return (
    <div className="px-6 py-6 lg:px-10">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Plans</h1>
        <p className="text-sm text-muted-foreground">
          Tiers d&apos;abonnement (starter, pro, enterprise) et quotas associés.
        </p>
      </header>

      {loadError && (
        <div className="rounded-md border border-red-200 bg-danger-subtle px-4 py-3 text-sm text-danger">
          Impossible de charger les plans : {loadError}
        </div>
      )}

      {!loadError && rows.length === 0 && (
        <div className="rounded-md border border-border bg-surface px-4 py-8 text-center text-sm text-muted-foreground">
          Aucun plan configuré.
        </div>
      )}

      {!loadError && rows.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((p) => {
            const features = parseFeatures(p.features);
            return (
              <article
                key={p.id}
                className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-5"
              >
                <header className="flex items-baseline justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">{p.name}</h2>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                      {p.code}
                    </p>
                  </div>
                  <Badge tone={p.visible ? "success" : "neutral"}>
                    {p.visible ? "Visible" : "Caché"}
                  </Badge>
                </header>

                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-semibold tabular-nums">
                    {priceFmt.format(Number(p.monthly_price))}
                  </span>
                  <span className="text-xs text-muted-foreground">/mois</span>
                  <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                    {priceFmt.format(Number(p.yearly_price))}/an
                  </span>
                </div>

                <dl className="grid grid-cols-2 gap-2 border-t border-border pt-3 text-xs">
                  <div>
                    <dt className="text-muted-foreground">Techniciens</dt>
                    <dd className="font-medium tabular-nums">
                      {p.max_technicians}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Sites</dt>
                    <dd className="font-medium tabular-nums">{p.max_sites}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Machines</dt>
                    <dd className="font-medium tabular-nums">
                      {p.max_machines}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Templates</dt>
                    <dd className="font-medium tabular-nums">
                      {p.max_templates}
                    </dd>
                  </div>
                </dl>

                {features.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 border-t border-border pt-3">
                    {features.map((f) => (
                      <span
                        key={f}
                        className="inline-block rounded border border-border bg-surface-2 px-2 py-0.5 text-[11px] text-foreground"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                )}

                <footer className="mt-auto border-t border-border pt-3 text-xs text-muted-foreground">
                  <span className="font-semibold tabular-nums text-foreground">
                    {p.active_subscriptions}
                  </span>{" "}
                  abonnement{p.active_subscriptions > 1 ? "s" : ""} actif
                  {p.active_subscriptions > 1 ? "s" : ""}
                </footer>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
